import type { ChangeEvent, FunctionComponent, InputHTMLAttributes } from 'react';
import { useId } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './Toggle.module.css';

export enum ToggleScale {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface ToggleProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The value by which the component scales. Default is 1 (no scale) */
  scale?: ToggleScale;
}

export const Toggle: FunctionComponent<ToggleProps> = ({
  checked,
  id,
  scale = 'medium',
  disabled,
  onChange,
  ...rest
}) => {
  const audioTrigger = useAudioTrigger();
  const reactCreatedId = useId();
  const anchorId = id || reactCreatedId;

  const mods = useModifiers(
    styles,
    'toggle',
    {
      toggle_disabled: disabled,
      [scale]: true,
    },
    { withBaseClass: true }
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(event);
    audioTrigger.play({ name: 'menuClick' });
  };

  return (
    <div data-testid="toggle" className={mods}>
      <input
        className={styles.toggle__input}
        type="checkbox"
        checked={checked}
        id={anchorId}
        disabled={disabled}
        onChange={handleChange}
        {...rest}
      />
      <label htmlFor={anchorId} className={styles.toggle__track}>
        <span className={styles.toggle__handle}></span>
      </label>
    </div>
  );
};
